import React, { useEffect, useRef, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import CreativePerformancePage from './index';
import TagsTab from './tags/TagsTab';
import InsightsTab from './insights/InsightsTab';

// Same shape as pages/VideoCreative/index.tsx: each tab owns a sub-path, tabs are
// mounted on first visit and then kept mounted (hidden) so switching back costs
// no refetch and keeps filters and scroll where they were. The Performance tab's
// query string is remembered across tab switches (see performanceSearch).
const TAB_ROUTES: {
  path: string;
  label: string;
  component: React.ComponentType<{ active: boolean }>;
}[] = [
  {
    path: 'performance',
    label: 'Performance',
    component: CreativePerformancePage,
  },
  { path: 'insights', label: 'Insights', component: InsightsTab },
  { path: 'tags', label: 'Tags', component: TagsTab },
];

export default function CreativePerformanceTabs() {
  const { url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  const activeIndex = TAB_ROUTES.findIndex(t =>
    location.pathname.startsWith(`${url}/${t.path}`),
  );
  const active = activeIndex === -1 ? 0 : activeIndex;

  // Bare /creative-performance (every existing bookmark and shared link) opens
  // the Performance tab with its query string intact.
  useEffect(() => {
    if (
      activeIndex === -1 &&
      (location.pathname === url || location.pathname === `${url}/`)
    ) {
      history.replace(`${url}/${TAB_ROUTES[0].path}${location.search}`);
    }
  }, [activeIndex, location.pathname, location.search, url, history]);

  // The Performance tab keeps its filters (dates, rules, sort) in the query
  // string. Other tabs have their own, so remember Performance's last one and
  // link back to it; otherwise a round trip would reset Performance to defaults.
  const performanceSearch = useRef(active === 0 ? location.search : '');
  if (active === 0) performanceSearch.current = location.search;

  const [mounted, setMounted] = useState<Set<number>>(() => new Set([active]));
  useEffect(() => {
    setMounted(prev => (prev.has(active) ? prev : new Set([...prev, active])));
  }, [active]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ px: 2, pt: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Creative Performance
        </Typography>
        <Tabs value={active} sx={{ borderBottom: '1px solid #E2E8F0' }}>
          {TAB_ROUTES.map(t => (
            <Tab
              key={t.path}
              label={t.label}
              component={Link}
              to={`${url}/${t.path}${t.path === 'performance' ? performanceSearch.current : ''}`}
            />
          ))}
        </Tabs>
      </Box>
      {TAB_ROUTES.map((t, i) => {
        if (!mounted.has(i)) return null;
        const Comp = t.component;
        return (
          <Box
            key={t.path}
            sx={{
              display: i === active ? 'block' : 'none',
              px: t.path === 'performance' ? 0 : 2,
              py: t.path === 'performance' ? 0 : 2,
            }}
          >
            <Comp active={i === active} />
          </Box>
        );
      })}
    </Box>
  );
}
